import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';

const FAQSection = styled.section`
  padding: 10rem 0;
  background: #f9fafb;

  @media (max-width: 768px) {
    padding: 5rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 3rem;
  color: #000;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FAQQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  font-size: 1.1rem;
  color: #000;

  &:hover {
    color: #253ec6;
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  max-height: ${(props) => (props.isOpen ? '200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-top: ${(props) => (props.isOpen ? '1rem' : '0')};
  color: #666;
  line-height: 1.6;
  margin-left: 0;

  a {
    color: #253ec6;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      text-decoration: none;
    }
  }
`;

const ToggleIcon = styled.span<{ isOpen: boolean }>`
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  color: #666;
`;

const faqs = [
  {
    question: 'Who comes to my home?',
    answer:
      'Verified, trained pros coordinated with your building. Every cleaner is background-checked, insured, and trained to our high standards.',
  },
  {
    question: 'Do I need to be home?',
    answer:
      'No! We coordinate with your building staff and management, so you never need to be home. Our professionals handle everything seamlessly.',
  },
  {
    question: 'Are there hourly minimums?',
    answer:
      'No. Tasks are priced per task. You only pay for exactly what you need, with transparent pricing and no surprise fees.',
  },
  {
    question: 'Can I customize?',
    answer:
      'Yes. Add notes and preferences for next time. We remember your specific needs and preferences to provide consistent, personalized service.',
  },
  {
    question: 'How is housekeeping priced?',
    answer:
      'By apartment size, room count, and number of bathrooms. We provide upfront, transparent pricing based on your specific space and cleaning frequency.',
  },
  {
    question: 'What types of services are included in the Housekeeping Plan?',
    answer:
      'Our Housekeeping Plan includes four cleaning types so you can choose the right level for each visit: Standard, Special Mission, Super Clean, and Moving In/Out Cleaning. View the full checklists here: [Checklist 1](https://heyhomero.com/services) and [Checklist 2](https://heyhomero.com/moving).',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // First FAQ open by default

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <FAQSection id="faq">
      <div className="container">
        <SectionTitle>FAQs</SectionTitle>

        <FAQContainer>
          {faqs.map((faq, index) => (
            <FAQCard key={index}>
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                <span>{faq.question}</span>
                <ToggleIcon isOpen={openIndex === index}>
                  {openIndex === index ? '^' : 'v'}
                </ToggleIcon>
              </FAQQuestion>
              <FAQAnswer isOpen={openIndex === index}>
                <ReactMarkdown>{faq.answer}</ReactMarkdown>
              </FAQAnswer>
            </FAQCard>
          ))}
        </FAQContainer>
      </div>
    </FAQSection>
  );
}
